/**
 * What a brand sells — master-list categories from `src/types/brands.ts`
 * plus scraper aliases. Keep aligned with the Brand Identifier Formats canvas.
 */

/** Master-list `categories` plus scraper aliases. */
export const BRAND_SELLS: Record<string, string[]> = {
  canon: ['cameras', 'lenses', 'accessories', 'lighting', 'audio', 'storage', 'software'],
  nikon: ['cameras', 'lenses', 'accessories', 'lighting', 'software'],
  sony: ['cameras', 'lenses', 'accessories', 'audio', 'storage', 'monitors'],
  fujifilm: ['cameras', 'lenses', 'accessories', 'instant_cameras', 'film_cameras'],
  panasonic: ['cameras', 'lenses', 'accessories', 'audio', 'monitors'],
  omsystem: ['cameras', 'lenses', 'accessories'],
  'om-system': ['cameras', 'lenses', 'accessories'],
  'ricoh-imaging': ['cameras', 'lenses', 'accessories'],
  pentax: ['cameras', 'lenses', 'accessories'],
  ricoh: ['cameras', 'accessories'],
  leica: ['cameras', 'lenses', 'accessories'],
  hasselblad: ['cameras', 'lenses', 'accessories'],
  phase_one: ['cameras', 'lenses', 'accessories', 'software'],
  gopro: ['action_cameras', 'accessories', 'software'],
  dji: ['drones', 'action_cameras', 'gimbals', 'accessories'],
  insta360: ['action_cameras', '360_cameras', 'accessories', 'software'],
  akaso: ['action_cameras', 'accessories'],
  yi: ['action_cameras', 'security_cameras', 'accessories'],
  zeiss: ['lenses', 'accessories', 'monitors'],
  sigma: ['lenses', 'accessories'],
  tamron: ['lenses', 'accessories'],
  tokina: ['lenses', 'accessories'],
  rokinon: ['lenses', 'accessories'],
  laowa: ['lenses', 'accessories'],
  viltrox: ['lenses', 'lighting', 'accessories'],
  voigtlander: ['lenses', 'accessories'],
  profoto: ['lighting', 'accessories'],
  broncolor: ['lighting', 'accessories'],
  elinchrom: ['lighting', 'accessories'],
  godox: ['lighting', 'accessories'],
  westcott: ['lighting', 'accessories', 'backgrounds', 'reflectors', 'diffusers'],
  aputure: ['lighting', 'accessories'],
  deity: ['audio', 'accessories', 'wireless_systems'],
  gitzo: ['tripods', 'monopods', 'heads', 'accessories'],
  manfrotto: ['tripods', 'monopods', 'heads', 'accessories', 'lighting'],
  really_right_stuff: ['tripods', 'heads', 'plates', 'brackets', 'accessories'],
  arca_swiss: ['tripods', 'heads', 'plates', 'brackets'],
  induro: ['tripods', 'monopods', 'heads', 'accessories'],
  benro: ['tripods', 'monopods', 'heads', 'accessories'],
  smallrig: ['cages', 'tripods', 'handles', 'grips', 'shoulder_rigs', 'mounts', 'lighting', 'accessories'],
  lowepro: ['bags', 'backpacks', 'messenger_bags', 'hard_cases', 'soft_cases'],
  think_tank: ['bags', 'backpacks', 'messenger_bags', 'rolling_cases'],
  pelican: ['hard_cases', 'pelican_cases', 'protective_cases'],
  tenba: ['bags', 'backpacks', 'messenger_bags', 'rolling_cases'],
  kata: ['bags', 'backpacks', 'messenger_bags'],
  peak_design: ['bags', 'straps', 'accessories', 'tripods'],
  sandisk: ['memory_cards', 'storage', 'accessories'],
  lexar: ['memory_cards', 'storage', 'accessories'],
  sony_memory: ['memory_cards', 'storage'],
  kingston: ['memory_cards', 'storage'],
  transcend: ['memory_cards', 'storage'],
  b_w: ['filters', 'accessories'],
  hoya: ['filters', 'accessories'],
  tiffen: ['filters', 'accessories'],
  lee_filters: ['filters', 'accessories'],
  cokin: ['filters', 'accessories'],
  nisi: ['filters', 'lenses', 'accessories'],
  rockynook: ['books'],
  kodak: ['film_cameras', 'instant_cameras', 'accessories'],
  ilford: ['accessories'],
  kentmere: ['accessories'],
  cinestill: ['accessories'],
  dubble_film: ['accessories'],
  lomography: ['film_cameras', 'instant_cameras', 'accessories'],
  minolta: ['film_cameras', 'lenses', 'accessories'],
  polaroid: ['instant_cameras', 'accessories'],
  print_file: ['storage', 'accessories'],
  agfa: ['film_cameras', 'accessories'],
  escura: ['film_cameras', 'instant_cameras', 'accessories'],
  blackmagic: ['video_cameras', 'monitors', 'accessories', 'software'],
  red: ['video_cameras', 'accessories', 'software'],
  arri: ['video_cameras', 'lenses', 'lighting', 'accessories'],
  atomos: ['monitors', 'recorders', 'accessories'],
  smallhd: ['monitors', 'accessories'],
  promaster: ['accessories', 'tripods', 'bags', 'filters', 'lenses'],
  vivitar: ['accessories', 'lenses', 'flash', 'filters'],
  quantaray: ['accessories', 'lenses', 'filters'],
  opteka: ['accessories', 'lenses', 'filters', 'tripods'],
  meike: ['accessories', 'lenses', 'filters'],
  superior_seamless: ['backgrounds', 'accessories'],
};

export function formatSellLabel(value: string): string {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function brandSells(brandId?: string | null, brand?: string | null): string[] {
  const keys = [brandId, brand]
    .map((value) =>
      String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_'),
    )
    .filter(Boolean);
  for (const key of keys) {
    if (BRAND_SELLS[key]) return BRAND_SELLS[key];
    const dashed = key.replace(/_/g, '-');
    if (BRAND_SELLS[dashed]) return BRAND_SELLS[dashed];
  }
  return [];
}
